import settings from "../../../../constants/settings";
import useSWRFetcher, { SWRFetchError } from "../fetcher";
import useSWR from "swr";
import {
  Board,
  BoardContext,
  BoardImages,
  BoardMachinesStates,
  FabDrawingsStatus,
  imagesForSoldermaskColor,
  imagesLoaded,
  PhysicalDetailsState,
  updateFabDrawingsState,
} from "../../../../reducers/uploadedBoard";
import { useCallback, useContext, useEffect, useMemo } from "react";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "./state-machine/reviewBoardFlowLoader";
import { useAppDispatch, useAppSelector } from "../../store";

/**
 * The board's images with the top and bottom previews in the board's
 * currently selected soldermask color.
 */
export function useBoardImagesForSelectedSoldermask(
  board: Board,
): BoardImages | null {
  const images = useAppSelector((state) => {
    return (state.uploadedBoard.boards[board.id] as BoardContext).images;
  });
  const soldermaskColor = board.physical_details?.soldermask_color ?? null;
  return useMemo(() => {
    if (images === null) {
      return null;
    }
    return imagesForSoldermaskColor(images, soldermaskColor);
  }, [images, soldermaskColor]);
}

export function useBoardImages() {
  const { boardId } = useContext(BoardIdContext) as BoardShipmentIds;
  const dispatch = useAppDispatch();

  const fetcher = useSWRFetcher<BoardImages>(undefined);

  const { data: images, mutate } = useSWR<BoardImages, SWRFetchError>(
    `${settings.api_url}/boards/${boardId}/images`,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  useEffect(() => {
    if (images !== undefined) {
      dispatch(imagesLoaded({ boardId, images }));
    }
  }, [boardId, dispatch, images]);

  const handleImageStateUpdates = useCallback(
    (nextStatesData: BoardMachinesStates, states: BoardMachinesStates) => {
      if (
        states.physical_details_state == PhysicalDetailsState.PROCESSING &&
        nextStatesData.physical_details_state == PhysicalDetailsState.PROCESSED
      ) {
        void mutate();
      } else if (
        states.fab_drawing_status == FabDrawingsStatus.PROCESSING &&
        nextStatesData.fab_drawing_status == FabDrawingsStatus.DRAWN
      ) {
        void mutate();
      } else {
        dispatch(
          updateFabDrawingsState({
            boardId,
            fabDrawingsStatus: nextStatesData.fab_drawing_status,
          }),
        );
      }
    },
    [boardId, dispatch, mutate],
  );

  return {
    handleImageStateUpdates,
  };
}
